import { ChevronRight } from "lucide-react";
import { Fragment } from "react";
import { DataValuesTable } from "@/components/contracts/DataValuesTable";
import { FacilitiesTable } from "@/components/contracts/FacilitiesTable";
import { FieldGrid } from "@/components/contracts/FieldGrid";
import { IncludedItemsTable } from "@/components/contracts/IncludedItemsTable";
import { PickupsTable } from "@/components/contracts/PickupsTable";
import { SetdownsTable } from "@/components/contracts/SetdownsTable";
import { formatCellValue } from "@/components/contracts/table-utils";
import { VehiclesTable } from "@/components/contracts/VehiclesTable";
import { DataTable } from "@/components/shared/data-table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ContractMovement } from "@/lib/coach-manager/types";
import { cn } from "@/lib/utils";

interface MovementsTableProps {
  movements: ContractMovement[];
  /** Which movement's accordion row (field grid + nested tabs) is open. Only one at a time. */
  expandedMovementId: number | null;
  onToggle: (movement: ContractMovement) => void;
}

function movementSummary(movement: ContractMovement): string {
  return (
    movement.MovementDescription ||
    [movement.Pickup, movement.Destination].filter(Boolean).join(" → ") ||
    `Movement ${movement.ContractMovementID}`
  );
}

/**
 * A movement's nested tables — vehicles/drivers, pickups, setdowns,
 * facilities, included items, and its own data — as pill tab toggles, one
 * category visible at a time. Only categories with data get a tab. Rendered
 * only while its row's accordion is open, so a fresh mount (fresh tab state)
 * happens naturally every time a different movement is expanded — no key
 * trick needed.
 */
function MovementNestedTables({ movement }: { movement: ContractMovement }) {
  const categories = [
    {
      value: "vehicles",
      label: "Vehicles",
      count: movement.ContractVehicles?.length ?? 0,
      render: () => <VehiclesTable vehicles={movement.ContractVehicles ?? []} />,
    },
    {
      value: "pickups",
      label: "Pickups",
      count: movement.ContractPickups?.length ?? 0,
      render: () => <PickupsTable pickups={movement.ContractPickups ?? []} />,
    },
    {
      value: "setdowns",
      label: "Setdowns",
      count: movement.ContractSetdowns?.length ?? 0,
      render: () => <SetdownsTable setdowns={movement.ContractSetdowns ?? []} />,
    },
    {
      value: "facilities",
      label: "Facilities",
      count: movement.ContractFacilities?.length ?? 0,
      render: () => <FacilitiesTable facilities={movement.ContractFacilities ?? []} />,
    },
    {
      value: "includedItems",
      label: "Included Items",
      count: movement.ContractIncludedItems?.length ?? 0,
      render: () => <IncludedItemsTable items={movement.ContractIncludedItems ?? []} />,
    },
    {
      value: "movementData",
      label: "Movement Data",
      count: movement.ContractMovementData?.length ?? 0,
      render: () => <DataValuesTable rows={movement.ContractMovementData ?? []} />,
    },
  ].filter((category) => category.count > 0);

  if (categories.length === 0) {
    return <p className="text-sm text-muted-foreground">This movement has no nested data.</p>;
  }

  return (
    <Tabs defaultValue={categories[0]?.value}>
      <TabsList>
        {categories.map((category) => (
          <TabsTrigger key={category.value} value={category.value}>
            {category.label} ({category.count})
          </TabsTrigger>
        ))}
      </TabsList>
      {categories.map((category) => (
        <TabsContent key={category.value} value={category.value}>
          {category.render()}
        </TabsContent>
      ))}
    </Tabs>
  );
}

/**
 * Full-width movements table: critical columns only, plus one expandable
 * accordion row per movement containing its full field grid AND its
 * Vehicles/Pickups/Setdowns/etc. tabs together, inline. Only one movement's
 * accordion is open at a time (`expandedMovementId`); clicking an open row
 * again collapses it, clicking a different row switches to it.
 */
export function MovementsTable({ movements, expandedMovementId, onToggle }: MovementsTableProps) {
  return (
    <DataTable
      headers={[
        { label: "", className: "w-8" },
        { label: "Movement" },
        { label: "Pickup Time" },
        { label: "Passengers" },
        { label: "Vehicles" },
        { label: "Pickups" },
        { label: "Setdowns" },
        { label: "Facilities" },
        { label: "Status" },
      ]}
      empty={<span className="text-sm text-muted-foreground">No movements.</span>}
    >
      {movements.map((movement) => {
        const isExpanded = expandedMovementId === movement.ContractMovementID;
        return (
          <Fragment key={movement.ContractMovementID}>
            <tr
              className={cn(
                "cursor-pointer border-b last:border-b-0 hover:bg-muted/40",
                isExpanded && "bg-[var(--bg-accent)]",
              )}
              onClick={() => onToggle(movement)}
            >
              <td className="px-2 py-2">
                <ChevronRight
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform",
                    isExpanded && "rotate-90",
                  )}
                />
              </td>
              <td className="px-4 py-2">{movementSummary(movement)}</td>
              <td className="px-4 py-2">{formatCellValue(movement.PickupTime)}</td>
              <td className="px-4 py-2">{movement.Passengers}</td>
              <td className="px-4 py-2">{movement.ContractVehicles?.length || "—"}</td>
              <td className="px-4 py-2">{movement.ContractPickups?.length || "—"}</td>
              <td className="px-4 py-2">{movement.ContractSetdowns?.length || "—"}</td>
              <td className="px-4 py-2">{movement.ContractFacilities?.length || "—"}</td>
              <td className="px-4 py-2">
                {movement.Cancelled ? (
                  <Badge variant="destructive">Cancelled</Badge>
                ) : (
                  <Badge variant="success">Active</Badge>
                )}
              </td>
            </tr>
            {isExpanded && (
              <tr className="border-b bg-[var(--bg-2)] last:border-b-0">
                <td colSpan={9} className="px-4 py-3">
                  <div className="space-y-4">
                    <FieldGrid
                      fields={[
                        { label: "Movement ID", value: movement.ContractMovementID },
                        { label: "Movement Description", value: movement.MovementDescription },
                        { label: "Start Base Serial No", value: movement.StartBaseSerialNo },
                        { label: "Start Time", value: movement.StartTime },
                        { label: "Position Day", value: movement.PositionDay },
                        { label: "Position Time", value: movement.PositionTime },
                        { label: "Pickup Day", value: movement.PickupDay },
                        { label: "Pickup Time", value: movement.PickupTime },
                        { label: "Pickup", value: movement.Pickup },
                        { label: "Pickup Latitude", value: movement.PickupLatitude },
                        { label: "Pickup Longitude", value: movement.PickupLongitude },
                        { label: "Pickup Instructions", value: movement.PickupInstructions },
                        { label: "Single Journey", value: movement.SingleJourney },
                        { label: "Vehicle To Stay", value: movement.VehicleToStay },
                        { label: "Destination", value: movement.Destination },
                        { label: "Destination Latitude", value: movement.DestinationLatitude },
                        { label: "Destination Longitude", value: movement.DestinationLongitude },
                        {
                          label: "Destination Instructions",
                          value: movement.DestinationInstructions,
                        },
                        { label: "Arrival Day", value: movement.ArrivalDay },
                        { label: "Arrival Time", value: movement.ArrivalTime },
                        { label: "Leave Day", value: movement.LeaveDay },
                        { label: "Leave Time", value: movement.LeaveTime },
                        { label: "Back Day", value: movement.BackDay },
                        { label: "Back Time", value: movement.BackTime },
                        { label: "Finish Day", value: movement.FinishDay },
                        { label: "Finish Time", value: movement.FinishTime },
                        { label: "Finish Base Serial No", value: movement.FinishBaseSerialNo },
                        { label: "Passengers", value: movement.Passengers },
                        { label: "Distance", value: movement.Distance },
                        { label: "Empty Distance", value: movement.EmptyDistance },
                        { label: "Client Reference 1", value: movement.ClientReference1 },
                        { label: "Client Reference 2", value: movement.ClientReference2 },
                        { label: "Route", value: movement.Route },
                        { label: "Further Requirements", value: movement.FurtherRequirements },
                        { label: "Notes", value: movement.Notes },
                        { label: "Last Updated", value: movement.LastUpdated },
                      ]}
                    />
                    <MovementNestedTables movement={movement} />
                  </div>
                </td>
              </tr>
            )}
          </Fragment>
        );
      })}
    </DataTable>
  );
}
